/**
 * 화면 한 대의 그림 — 모니터링 ▸ 사업장 현황의 화면 노드·화면 판이 쓴다.
 *
 * 2026-10-22 까지는 화면 멀티뷰(`screens/Wall.tsx`)의 카드 그림이었다. 멀티뷰를 지우면서(사용자 지시 "모니터링에서
 * 화면 멀티뷰는 지우고 사업장 현황 하나로") 사업장 현황이 함께 쓰던 이 부분만 남겼다 — 꺼진 화면을 붉은 오프라인
 * 자리로, 켜진 화면을 마지막 보고 그림(없으면 편성)으로 그리는 규칙은 그대로다.
 */
import { useState, type ReactNode } from 'react';
import { FileCode, Image as ImageIcon, MonitorX, Play, Video } from 'lucide-react';
import { headerPlace } from '@signageone/core';
import type { ScreenReport, WallCard } from '../api/client.js';
import { relativeTime } from './ui.js';
import { TemplatePreview } from './TemplatePreview.js';
import { useBrand } from './brand.js';
import { HtmlFrame } from './HtmlContent.js';
import { LiveScreen } from './LiveScreen.js';
import { captureVideoUrl, earlierScreen, type WallCapture } from './screenCapture.js';
import { formatOf, type OutputFormat } from './screenFormat.js';

/** 마지막 보고에서 몇 초가 지났나 — 목록을 받은 시각(`asOf`)에서 잰다. 둘 중 하나라도 없으면 `null`(모른다). */
export function silentSeconds(lastHeartbeatAt: string | null, asOf: string | null): number | null {
  if (!lastHeartbeatAt || !asOf) return null;
  const last = new Date(lastHeartbeatAt).getTime();
  const at = new Date(asOf).getTime();
  if (Number.isNaN(last) || Number.isNaN(at)) return null;
  // 시계가 어긋나 미래로 보이면 0 으로 둔다 — 음수를 "방금" 이라고 읽는 것이 덜 틀린다.
  return Math.max(0, Math.round((at - last) / 1000));
}

/**
 * 조용한 시간을 짧게 — `47초` · `3분` · `2시간`.
 *
 * `relativeTime` 은 "3분 전" 처럼 지난 일을 말하는 말투라 "…째" 와 붙지 않는다.
 * 여기서는 **얼마나 이어지고 있나**를 말한다.
 */
export function silentLabel(sec: number | null): string {
  if (sec === null) return '한참';
  if (sec < 60) return `${sec}초`;
  if (sec < 3600) return `${Math.floor(sec / 60)}분`;
  if (sec < 86_400) return `${Math.floor(sec / 3600)}시간`;
  return `${Math.floor(sec / 86_400)}일`;
}

/**
 * 화면 한 대의 그림 — 사업장 현황의 화면 노드와 화면 판이 쓴다. 꺼진 화면은 오프라인 자리, 켜진 화면은 마지막 보고
 * 그림, 없으면 편성으로 그린 것이다. 새 캡처를 청하지 않는다 — 노드를 그리는 것만으로 화면 전부가 찍어 올리면 안
 * 된다. 상자를 채우게 두고 화면의 실제 비는 `ScreenShape` 가 지킨다.
 */
export function DisplayThumbnail({
  card,
  asOf,
  stageRatio = 16 / 9,
}: {
  card: WallCard;
  asOf: string | null;
  stageRatio?: number;
}): ReactNode {
  const [gone, setGone] = useState<string | null>(null);
  // 서버가 "갓 찍은 것" 으로 준 그림(`screen`)은 캡처처럼 또렷이, 낡은 것(`last_screen`)은 이전 모습으로 흐리게.
  const fresh = card.online && card.screen && card.screen.taken_at !== gone ? card.screen : null;
  const capture = fresh ? { report: fresh, videoUrl: captureVideoUrl(fresh, card.now_playing) } : null;
  const earlier =
    card.online && !capture
      ? earlierScreen(
          card.last_screen,
          card.last_screen ? captureVideoUrl(card.last_screen, card.now_playing) : null,
          gone,
        )
      : null;
  return (
    <div className="wall-stage h-full bg-slate-900">
      <ScreenShape
        card={card}
        format={formatOf(card)}
        stageRatio={stageRatio}
        capture={capture}
        earlier={earlier}
        asOf={asOf}
        onCaptureMissing={(_id, report) => setGone(report.taken_at)}
        onEarlierMissing={(report) => setGone(report.taken_at)}
      />
    </div>
  );
}

/**
 * 화면의 실제 모양.
 *
 * 카드 상자와 화면의 비가 다르면 **상자를 채우지 않는다.** 세로 패널은 가운데 세워
 * 두고 양옆을 비운다 — 그 빈 자리가 "이 화면은 세로다" 라고 말한다. 늘여서 채우면
 * 모양이 거짓이 되고, 잘라서 채우면 세로인 것이 감춰진다.
 *
 * 어느 변을 상자에 맞출지는 여기서 정한다. CSS 로 `max-width`·`max-height` 를 함께
 * 걸면 브라우저가 비를 깨뜨리는 쪽으로 푼다.
 */
function ScreenShape({
  card,
  format,
  stageRatio,
  capture,
  earlier,
  asOf,
  onCaptureMissing,
  onEarlierMissing,
}: {
  card: WallCard;
  format: OutputFormat;
  stageRatio: number;
  capture: WallCapture | null;
  earlier: ScreenReport | null;
  asOf: string | null;
  onCaptureMissing: (id: string, report: ScreenReport) => void;
  onEarlierMissing: (report: ScreenReport) => void;
}): ReactNode {
  const bound = format.ratio >= stageRatio ? { width: '100%' } : { height: '100%' };

  return (
    <div className="wall-shot" style={{ aspectRatio: format.ratio, ...bound }}>
      {/*
        **꺼져 있는 화면에는 그림을 걸지 않는다.**

        예전에는 마지막으로 찍힌 그림을 45% 로 흐려서 걸었다. 그런데 벽은 그림부터 읽는
        물건이라, 흐린 그림은 "연결이 시원찮은가 보다" 로 읽히지 "이 화면은 꺼져 있다" 로는
        읽히지 않았다 — 실제로 꺼진 화면을 며칠 지나 발견하는 일이 여기서 나왔다.
        그림 자리를 통째로 오프라인이라고 말하는 데 쓴다.
      */}
      {card.online ? (
        <NowPlaying
          card={card}
          capture={capture}
          earlier={earlier}
          onCaptureMissing={onCaptureMissing}
          onEarlierMissing={onEarlierMissing}
        />
      ) : (
        <OfflineShot card={card} asOf={asOf} />
      )}
    </div>
  );
}

/**
 * 꺼져 있는 화면의 그림 자리.
 *
 * 꺼진 패널처럼 어둡게 두고, 언제부터 소식이 없는지를 함께 적는다 — 방금 껐는지 사흘째
 * 꺼져 있는지가 조치를 가른다. 아래 띠에도 같은 시각이 있지만 거기서는 위치에 묻히고,
 * 벽을 훑는 눈은 그림 자리에 먼저 닿는다.
 */
function OfflineShot({ card, asOf }: { card: WallCard; asOf: string | null }): ReactNode {
  const silent = silentSeconds(card.status.last_heartbeat_at, asOf);
  return (
    /*
      **꺼진 패널이 아니라 조치가 필요한 자리로 읽히게 한다(2026-09-21).**

      예전에는 어두운 회색 바탕에 작은 글자였다. 한두 장일 때는 읽혔지만 카드가 스무 장
      넘어가면 어두운 카드가 여럿이라(영상·밤 시간 화면) 꺼진 것이 묻혔다 — 사용자가 "오프라인
      인식이 잘 안 된다" 고 한 자리다. 붉은 바탕에 큰 글자로 바꾸고, 며칠째인지를 그림 자리에서
      바로 읽히게 한다.

      아래 여백은 **그림 위에 얹히는 띠를 비켜 주는 자리**다. 카드 높이를 사람이 정하게 되면서
      그림 자리가 낮아질 수 있고, 가운데 정렬만 두면 큰 글자가 띠의 글자와 겹쳐 둘 다 못 읽는다.
    */
    <div className="wall-offline flex h-full w-full flex-col items-center justify-center gap-1 bg-red-950/95 pb-7 text-red-100">
      <MonitorX className="h-8 w-8 text-red-300" strokeWidth={1.8} />
      <span className="text-[14px] font-semibold tracking-tight text-red-50">오프라인</span>
      <span className="wall-offline-detail text-[11.5px] text-red-200/90">
        {silent === null
          ? '보고 기록 없음'
          : `${silentLabel(silent)}째 · 마지막 보고 ${relativeTime(card.status.last_heartbeat_at, asOf ? new Date(asOf) : undefined)}`}
      </span>
    </div>
  );
}

/**
 * 카드의 그림 — 들어온 순간 집어 둔 화면, 아직 없으면 이전 모습, 그것도 없으면 지금 나가는
 * 항목으로 그린 것.
 *
 * 무엇을 걸었는지는 그림 아래 띠가 밝힌다(이전 모습·편성 기준). 캡처에는 아무 표시도 얹지
 * 않는다 — 멈춘 그림에 시각 표시를 얹으면 실시간 화면처럼 읽힌다.
 */
function NowPlaying({
  card,
  capture,
  earlier,
  onCaptureMissing,
  onEarlierMissing,
}: {
  card: WallCard;
  capture: WallCapture | null;
  earlier: ScreenReport | null;
  onCaptureMissing: (id: string, report: ScreenReport) => void;
  onEarlierMissing: (report: ScreenReport) => void;
}): ReactNode {
  const playing = card.now_playing;
  const { brand } = useBrand();

  /*
   * 들어온 순간 집어 둔 그림이 있으면 **그것이 먼저다.**
   *
   * 이 벽이 답해야 하는 질문은 "무엇이 나가야 하나"가 아니라 "무엇이 나가고 있나"다.
   * 자산 썸네일은 앞의 질문에만 답한다 — 화면이 검거나, 늘어났거나, 영상이 첫 프레임에
   * 멈춰 있거나, 아직 옛 편성을 틀고 있는 것은 썸네일로는 영영 보이지 않는다.
   *
   * 어느 보고를 집을지는 `screenCapture.ts` 한 곳이 정한다 — 화면 상세와 같은 규칙이다.
   * 영상도 틀지 않고 그 장면을 그림으로 떠내 건다. 카드마다 영상을 돌리면 화면 수만큼
   * 영상이 동시에 돌아가고, 벽이 답할 질문이 움직이는 그림 사이에 묻힌다.
   */
  if (capture) {
    return (
      <LiveScreen
        report={capture.report}
        videoUrl={capture.videoUrl}
        onMissing={(report) => onCaptureMissing(card.id, report)}
      />
    );
  }

  /*
   * 캡처가 아직이면 **전에 찍힌 그림을 먼저 건다.** 빈 자리로 20~40초를 기다리게 하면
   * 콘솔이 느린 것으로 읽힌다. 캡처가 오면 바뀌므로 한 번 껌뻑이지만, 그림 아래 띠가
   * "이전 모습" 이라 밝혀 두어 그 사이의 그림을 지금 화면으로 읽지 않는다.
   *
   * 조금 흐리게 건다 — 캡처와 나란히 놓였을 때 어느 카드가 아직인지 훑어 보인다.
   * 이전 모습이 없으면 아래로 내려가 편성으로 그린다(띠가 "편성 기준" 이라 밝힌다).
   */
  if (earlier) {
    return (
      <div className="h-full w-full opacity-75">
        <LiveScreen report={earlier} videoUrl={captureVideoUrl(earlier, playing)} onMissing={onEarlierMissing} />
      </div>
    );
  }

  // 집은 그림도 없고 나갈 것도 없을 때만 빈 자리를 그린다.
  if (!playing) {
    return (
      <Placeholder tone={card.playback.source === 'closed' ? 'neutral' : 'warn'}>
        <MonitorX className="h-6 w-6" strokeWidth={1.6} />
        <span>
          {card.playback.source === 'none'
            ? '배정된 편성 없음'
            : card.playback.source === 'closed'
              ? '운영 시간 외 — 쉬는 중'
              : '재생할 항목 없음'}
        </span>
      </Placeholder>
    );
  }

  if (playing.type === 'template' && playing.template) {
    /*
     * 상자를 채우게 둔다. 템플릿 캔버스는 언제나 16:9 지만, 렌더러가 남는 공간을
     * letterbox 로 채우므로 세로 상자에서도 잘리지 않는다 — 화면에서 일어나는 일과
     * 같다(플레이어도 회전을 안 걸면 여백을 남긴다).
     */
    return (
      <TemplatePreview
        template={playing.template}
        // 화면에 나가는 머리글 글 그대로 — 환경 설정의 방식(이름·위치·직접 입력·표시 안 함)대로.
        location={headerPlace(brand.place, card)}
        timezone={card.timezone ?? undefined}
        fill
      />
    );
  }

  if (playing.type === 'image' && playing.preview_url) {
    return <img src={playing.preview_url} alt="" className="h-full w-full object-cover" loading="lazy" />;
  }

  // 올린 HTML 은 출처 없는 샌드박스로 줄여 띄운다 — 화면 가까이 온 카드만(`HtmlContent.tsx`).
  if (playing.type === 'html' && playing.preview_url) {
    return (
      <HtmlFrame
        url={playing.preview_url}
        title={playing.title}
        className="h-full w-full bg-black"
        design={playing.width && playing.height ? { width: playing.width, height: playing.height } : null}
        fit={playing.fit}
      />
    );
  }

  return (
    <Placeholder tone="neutral">
      {playing.type === 'video' ? (
        <Video className="h-6 w-6" strokeWidth={1.6} />
      ) : playing.type === 'web' ? (
        <Play className="h-6 w-6" strokeWidth={1.6} />
      ) : playing.type === 'html' ? (
        <FileCode className="h-6 w-6" strokeWidth={1.6} />
      ) : (
        <ImageIcon className="h-6 w-6" strokeWidth={1.6} />
      )}
      <span className="line-clamp-2 px-3 text-center">{playing.title}</span>
    </Placeholder>
  );
}

function Placeholder({ tone, children }: { tone: 'neutral' | 'warn'; children: ReactNode }): ReactNode {
  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center gap-1.5 text-[12px] ${
        tone === 'warn' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-400'
      }`}
    >
      {children}
    </div>
  );
}
